import Snackbar from '@material-ui/core/Snackbar';
import MuiAlert from '@material-ui/lab/Alert';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSetModalTitle } from '../../../hooks/modal/useSetModalTitle.ts';
import { ChangePasswordResponse, useChangePasswordMutation } from '../../../hooks/useChangePasswordMutation.ts';
import screenNavigator from '../../../services/ScreenNavigator';
import Button from '../../bits/Button.jsx';
import PasswordTextField from '../../bits/PasswordTextField.jsx';
import MenuHeaderBar from '../MenuHeaderBar.jsx';

const Alert: React.FC<any> = (props) => {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
};

interface ChangePasswordProps { }

interface ResultMessageProps {
  message: string;
  severity: string;
}

const ChangePassword: React.FC<ChangePasswordProps> = () => {
  const [oldPassword, setOldPassword] = useState<string>('');
  const [newPassword, setNewPassword] = useState<string>('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState<string>('');
  const [resultMessage, setResultMessage] = useState<ResultMessageProps | null>(null);
  const mutation = useChangePasswordMutation();
  const exitIntervalId = useRef(null);
  const history = useHistory();

  useSetModalTitle('Change Password');

  // Ensure we cancel any timer on exit
  useEffect(() => () => clearTimeout(exitIntervalId.current), [exitIntervalId]);

  const submitEnabled = useMemo(() => oldPassword.length > 3
    && newPassword.length >= 6
    && newPasswordConfirm.length >= 6,
    [oldPassword, newPassword, newPasswordConfirm]);

  const onSave = useCallback(() => {
    if (newPassword != newPasswordConfirm) {
      setResultMessage({ message: 'New passwords don\'t match.', severity: 'error' });
      return;
    }

    mutation.mutate({ oldPassword, newPassword }, {
      onSuccess: (responsePayload: ChangePasswordResponse) => {
        if (!responsePayload.success) {
          setResultMessage({ message: 'Unable to change password.', severity: 'error' });
          return;
        }

        /**
         * I don't love the UX that happens here... its a success message followed by a 2 second
         * pause then the screen exits and returns to parent. I think better would be to
         * immediately exit and then display success on the parent... but that requires some
         * kind of global state.
         */
        setResultMessage({ message: 'Password changed successfully.', severity: 'success' });
        exitIntervalId.current = setTimeout(() => {
          screenNavigator.exitCurrentScreen(history);
        }, 2000);
      },
      onError: () => {
        setResultMessage({ message: 'Unable to change password.', severity: 'error' });
      },
    });
  }, [oldPassword, newPassword, newPasswordConfirm, exitIntervalId]);

  const onHideErrorBar = useCallback(() => setResultMessage(null), [setResultMessage]);

  return (
    <>
      <div>
        <PasswordTextField
          id="oldPassword"
          classes={{ root: 'login-field' }}
          label="Old password"
          value={oldPassword}
          margin="normal"
          disabled={mutation.isLoading}
          onChange={e => setOldPassword(e.target.value)}
        />

        <PasswordTextField
          id="newPassword"
          label="New password"
          value={newPassword}
          margin="normal"
          disabled={mutation.isLoading}
          onChange={e => setNewPassword(e.target.value)}
        />

        <PasswordTextField
          id="newPasswordConfirm"
          label="Confirm new password"
          value={newPasswordConfirm}
          margin="normal"
          disabled={mutation.isLoading}
          onChange={e => setNewPasswordConfirm(e.target.value)}
        />
        <p className="help">Make sure your new password is at least 6 characters long.</p>

        <div style={{ marginTop: '30px' }}>
          <Button
            onClick={onSave}
            color="primary"
            disabled={!submitEnabled || mutation.isLoading}
            loading={mutation.isLoading}>Save</Button>
        </div>
      </div>

      <Snackbar
        open={resultMessage !== null}
        autoHideDuration={6000}
        onClose={onHideErrorBar}>
        {resultMessage
          && <Alert onClose={onHideErrorBar} severity={resultMessage?.severity}>
            {resultMessage?.message || ''}
          </Alert>
        }
      </Snackbar>
    </>
  );
};

export {
  ChangePassword
};

